import React, { useState, useEffect, useRef } from "react";
// Tracking utility (replace with analytics/event API as needed)
const trackCartIconClick = () => {
  // Example: send to analytics or log
  if (window && window.dataLayer) {
    window.dataLayer.push({ event: "cart_icon_click" });
  } else {
    console.log("Cart icon clicked (tracking)");
  }
};
import { useCart } from "../utils/CartContext";
import { useAuth } from "../utils/AuthContext";
import CartDrawer from "./CartDrawer";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  User,
  Search,
  ChevronRight,
  X,
  ChevronLeft,
} from "lucide-react";
import WishlistIcon from "./WishlistIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsStaggered } from "@fortawesome/free-solid-svg-icons";
import * as catalogApi from "../api/catalog";

const logo =
  "https://ik.imagekit.io/je2vkmmlbm/logo.png";

const placeholderPhrases = [
  'Search "Sneakers"',
  'Search "Headphones"',
  'Search "Smart Watches"',
  'Search "Earbuds"',
];

const typingConfig = {
  typeSpeed: 95,
  deleteSpeed: 60,
  pauseAfterType: 1100,
  pauseAfterDelete: 400,
};

const highlightPlaceholderPieces = (text) => {
  if (!text) return { before: "", highlight: "", after: "" };
  const match = text.match(/"[^"]*"/);
  if (!match) return { before: text, highlight: "", after: "" };
  return {
    before: text.slice(0, match.index),
    highlight: match[0],
    after: text.slice(match.index + match[0].length),
  };
};

const navItems = [
  {
    name: "Trending Worldwide",
    path: "/collection/trending-worldwide",
    disableNavigation: true,
    submenu: [
      { name: "New Arrivals", path: "/collection/new-arrivals" },
      { name: "Best Sellers", path: "/collection/best-sellers" },
      { name: "Hot Deals", path: "/collection/hot-deals" },
      { name: "Recommended", path: "/recommended" },
    ],
  },
  {
    name: "Mens Fashion",
    path: "/collection/mens-fashion",
    submenu: [
      {
        name: "Clothing",
        path: "/collection/mens-clothing",
        submenu: [
          { name: "T-Shirts", path: "/collection/mens-tshirts" },
          { name: "Shirts", path: "/collection/mens-shirts" },
          { name: "Jeans", path: "/collection/mens-jeans" },
        ],
      },
      {
        name: "Shoes",
        path: "/collection/mens-shoes",
        submenu: [
          { name: "Casual", path: "/collection/mens-shoes-casual" },
          { name: "Formal", path: "/collection/mens-shoes-formal" },
        ],
      },
      { name: "Accessories", path: "/collection/mens-accessories" },
    ],
  },
  {
    name: "MakeUp",
    path: "/collection/makeup",
    submenu: [
      {
        name: "Lipsticks",
        path: "/collection/lipsticks",
        submenu: [
          { name: "Matte", path: "/collection/matte-lipsticks" },
          { name: "Liquid", path: "/collection/liquid-lipsticks" },
          { name: "Crayon", path: "/collection/crayon-lipsticks" },
        ],
      },
      { name: "Foundation", path: "/collection/foundation" },
      { name: "Eye Makeup", path: "/collection/eye-makeup" },
    ],
  },
  {
    name: "Body & Skin Care",
    path: "/collection/body-skin-care",
    submenu: [
      { name: "Face Care", path: "/collection/face-care" },
      { name: "Body Care", path: "/collection/body-care" },
      { name: "Hair Care", path: "/collection/hair-care" },
    ],
  },
  {
    name: "Gifting",
    path: "/collection/gifting",
    submenu: [
      { name: "Gift Sets", path: "/collection/gift-sets" },
      { name: "Birthday Gifts", path: "/collection/birthday-gifts" },
    ],
  },
  {
    name: "More",
    path: "/collection/more",
    submenu: [
      { name: "Fragrances", path: "/collection/fragrances" },
      { name: "Electronics", path: "/collection/electronics" },
    ],
  },
];

const MobileMenu = ({ isOpen, onClose, navItems }) => {
  const navigate = useNavigate();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [expandedNestedMenu, setExpandedNestedMenu] = useState(null);

  const handleMenuClick = (path) => {
    navigate(path);
    onClose();
    setExpandedMenu(null);
    setExpandedNestedMenu(null);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        ></div>
      )}

      <div
        className={`fixed top-0 left-0 h-screen w-80 bg-white z-50 transform transition-transform duration-300 ease-in-out md:hidden shadow-2xl overflow-y-auto ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Menu</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        <div className="p-4 space-y-2">
          {navItems.map((item, index) => (
            <div key={index}>
              <button
                onClick={() =>
                  expandedMenu === index
                    ? setExpandedMenu(null)
                    : setExpandedMenu(index)
                }
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-800 font-medium transition"
              >
                <span className="uppercase font-medium">{item.name}</span>
                <ChevronRight
                  size={18}
                  className={`transition-transform ${
                    expandedMenu === index ? "rotate-90" : ""
                  }`}
                />
              </button>

              {expandedMenu === index && item.submenu && (
                <div className="ml-4 space-y-1 bg-gray-50 rounded-lg p-2 mt-1">
                  {item.submenu.map((subItem, subIndex) => (
                    <div key={subIndex}>
                      <button
                        onClick={() =>
                          expandedNestedMenu === `${index}-${subIndex}`
                            ? setExpandedNestedMenu(null)
                            : setExpandedNestedMenu(`${index}-${subIndex}`)
                        }
                        className="w-full flex items-center justify-between px-4 py-2 rounded text-sm text-gray-700 hover:bg-white transition"
                      >
                        <span className="uppercase font-medium">
                          {subItem.name}
                        </span>
                        {subItem.submenu && (
                          <ChevronRight
                            size={16}
                            className={`transition-transform ${
                              expandedNestedMenu === `${index}-${subIndex}`
                                ? "rotate-90"
                                : ""
                            }`}
                          />
                        )}
                      </button>

                      {expandedNestedMenu === `${index}-${subIndex}` &&
                        subItem.submenu && (
                          <div className="ml-4 space-y-1 bg-white rounded p-2 mt-1">
                            {subItem.submenu.map((nestedItem, nestedIdx) => (
                              <button
                                key={nestedIdx}
                                onClick={() => handleMenuClick(nestedItem.path)}
                                className="w-full text-left px-3 py-2 text-xs text-gray-600 hover:bg-pink-50 hover:text-pink-600 rounded transition uppercase font-medium"
                              >
                                {nestedItem.name}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const NewNavbar = () => {
  const navigate = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeNestedDropdown, setActiveNestedDropdown] = useState(null);
  const { count } = useCart();
  const { token } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [placeholder, setPlaceholder] = useState("Search");
  const [animating, setAnimating] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [showRecommended, setShowRecommended] = useState(false);
  const [showRecommendedBuffer, setShowRecommendedBuffer] = useState(false);
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hoveredNavIndex, setHoveredNavIndex] = useState(null);
  const [submenuItemsVisible, setSubmenuItemsVisible] = useState(false);
  const [nestedSubmenuItemsVisible, setNestedSubmenuItemsVisible] =
    useState(false);
  const [navUnderline, setNavUnderline] = useState({
    x: 0,
    width: 0,
    visible: false,
  });

  const dropdownTimeout = useRef(null);
  const nestedTimeout = useRef(null);
  const searchTimeout = useRef(null);
  const recommendedTimeout = useRef(null);
  const navListRef = useRef(null);
  const navLinkRefs = useRef({});
  const animRef = useRef({
    phraseIndex: 0,
    charIndex: 0,
    mode: "typing",
    timeoutId: null,
  });

  useEffect(
    () => () => {
      clearTimeout(animRef.current.timeoutId);
      clearTimeout(dropdownTimeout.current);
      clearTimeout(nestedTimeout.current);
      clearTimeout(searchTimeout.current);
      clearTimeout(recommendedTimeout.current);
    },
    [],
  );

  // Listen for global cart drawer open event
  useEffect(() => {
    const handleOpenCartDrawer = () => setCartOpen(true);
    window.addEventListener("open-cart-drawer", handleOpenCartDrawer);
    return () => window.removeEventListener("open-cart-drawer", handleOpenCartDrawer);
  }, []);

  const fetchRecommendedProducts = async () => {
    setRecommendedLoading(true);
    try {
      const response = await catalogApi.listProducts({
        segment: "recommended",
      });
      const products = response?.data || response || [];
      setRecommendedProducts(Array.isArray(products) ? products : []);
    } catch (error) {
      console.error("Error fetching recommended products:", error);
      setRecommendedProducts([]);
    } finally {
      setRecommendedLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendedProducts();
  }, []);

  useEffect(() => {
    if (searchValue) {
      setAnimating(false);
      setShowRecommended(false);
      setShowRecommendedBuffer(false);
      if (recommendedTimeout.current) {
        clearTimeout(recommendedTimeout.current);
      }
      clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => {
        performSearch(searchValue);
      }, 300);
    } else {
      setAnimating(true);
      setShowSearchResults(false);
      setSearchResults([]);
    }
  }, [searchValue]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(
        `/api/search/suggestions?q=${encodeURIComponent(query)}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        const products = data.products || [];
        setSearchResults(products);
        setShowSearchResults(products.length > 0);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleProductClick = (productId) => {
    setSearchValue("");
    setShowSearchResults(false);
    setShowRecommended(false);
    setShowRecommendedBuffer(false);
    setMobileSearchOpen(false);
    navigate(`/product/${productId}`);
  };

  const handleSearchClear = () => {
    setSearchValue("");
    setShowSearchResults(false);
    setShowRecommended(false);
    setShowRecommendedBuffer(false);
    setSearchResults([]);
    if (recommendedTimeout.current) {
      clearTimeout(recommendedTimeout.current);
    }
  };

  const handleSearchFocus = () => {
    if (!searchValue) {
      setAnimating(false);
      setShowRecommendedBuffer(true);
      setShowRecommended(false);

      if (recommendedTimeout.current) {
        clearTimeout(recommendedTimeout.current);
      }

      recommendedTimeout.current = setTimeout(() => {
        setShowRecommendedBuffer(false);
        setShowRecommended(true);
      }, 2000);
    } else {
      setShowSearchResults(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      if (!searchValue) {
        setAnimating(true);
        setShowRecommended(false);
        setShowRecommendedBuffer(false);
        if (recommendedTimeout.current) {
          clearTimeout(recommendedTimeout.current);
        }
      }
    }, 200);
  };

  useEffect(() => {
    if (!animating) return;

    const animate = () => {
      const { phraseIndex, charIndex, mode } = animRef.current;
      const currentPhrase = placeholderPhrases[phraseIndex];

      if (mode === "typing") {
        const nextCharIndex = charIndex + 1;
        setPlaceholder(currentPhrase.slice(0, nextCharIndex));
        animRef.current.charIndex = nextCharIndex;

        if (nextCharIndex === currentPhrase.length) {
          animRef.current.mode = "pauseAfterType";
          animRef.current.timeoutId = setTimeout(
            animate,
            typingConfig.pauseAfterType,
          );
        } else {
          animRef.current.timeoutId = setTimeout(
            animate,
            typingConfig.typeSpeed,
          );
        }
        return;
      }

      if (mode === "pauseAfterType") {
        animRef.current.mode = "deleting";
        animRef.current.timeoutId = setTimeout(
          animate,
          typingConfig.deleteSpeed,
        );
        return;
      }

      if (mode === "deleting") {
        const prevCharIndex = charIndex - 1;
        setPlaceholder(currentPhrase.slice(0, prevCharIndex));
        animRef.current.charIndex = prevCharIndex;

        if (prevCharIndex === 0) {
          animRef.current.mode = "pauseAfterDelete";
          animRef.current.timeoutId = setTimeout(
            animate,
            typingConfig.pauseAfterDelete,
          );
        } else {
          animRef.current.timeoutId = setTimeout(
            animate,
            typingConfig.deleteSpeed,
          );
        }
        return;
      }

      if (mode === "pauseAfterDelete") {
        animRef.current.mode = "typing";
        animRef.current.phraseIndex =
          (phraseIndex + 1) % placeholderPhrases.length;
        animRef.current.timeoutId = setTimeout(animate, typingConfig.typeSpeed);
      }
    };

    animRef.current.timeoutId = setTimeout(animate, typingConfig.typeSpeed);

    return () => clearTimeout(animRef.current.timeoutId);
  }, [animating]);

  const handleDropdownEnter = (index) => {
    clearTimeout(dropdownTimeout.current);
    setActiveDropdown(index);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => {
      setActiveDropdown(null);
      setActiveNestedDropdown(null);
    }, 150);
  };

  const setNavLinkRef = (index, node) => {
    if (node) {
      navLinkRefs.current[index] = node;
      return;
    }
    delete navLinkRefs.current[index];
  };

  const updateNavUnderline = (index) => {
    const navListEl = navListRef.current;
    const navLinkEl = navLinkRefs.current[index];
    if (!navListEl || !navLinkEl) return;

    const listRect = navListEl.getBoundingClientRect();
    const linkRect = navLinkEl.getBoundingClientRect();

    setNavUnderline({
      x: linkRect.left - listRect.left,
      width: linkRect.width,
      visible: true,
    });
  };

  const handleNavItemEnter = (index) => {
    handleDropdownEnter(index);
    setHoveredNavIndex(index);
    updateNavUnderline(index);
  };

  const handleNavItemsLeave = () => {
    setHoveredNavIndex(null);
    setNavUnderline((prev) => ({ ...prev, width: 0, visible: false }));
  };

  const handleNestedEnter = (index) => {
    clearTimeout(nestedTimeout.current);
    setActiveNestedDropdown(index);
  };

  const handleNestedLeave = () => {
    nestedTimeout.current = setTimeout(() => {
      setActiveNestedDropdown(null);
    }, 150);
  };

  const updateScrollState = () => {
    const el = carouselRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  const scrollCarousel = (direction) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = 220;
    el.scrollBy({ left: direction * cardWidth, behavior: "smooth" });
  };

  useEffect(() => {
    updateScrollState();
  }, [recommendedProducts, showRecommended]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handler = () => updateScrollState();
    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (hoveredNavIndex === null) return;
      updateNavUnderline(hoveredNavIndex);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hoveredNavIndex]);

  useEffect(() => {
    if (activeDropdown === null) {
      setSubmenuItemsVisible(false);
      setNestedSubmenuItemsVisible(false);
      return;
    }

    setSubmenuItemsVisible(false);
    const rafId = window.requestAnimationFrame(() => {
      setSubmenuItemsVisible(true);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [activeDropdown]);

  useEffect(() => {
    if (activeNestedDropdown === null) {
      setNestedSubmenuItemsVisible(false);
      return;
    }

    setNestedSubmenuItemsVisible(false);
    const rafId = window.requestAnimationFrame(() => {
      setNestedSubmenuItemsVisible(true);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [activeNestedDropdown]);

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3 md:gap-10 min-w-max">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition"
                aria-label="Open menu"
              >
                <FontAwesomeIcon
                  icon={faBarsStaggered}
                  size="lg"
                  className="text-gray-700"
                />
              </button>

              <Link to="/">
                <img
                  src={logo}
                  alt="Logo"
                  className="h-12 md:h-14 w-auto object-contain hover:opacity-80 transition-opacity"
                  style={{ maxHeight: "80px", padding: "2px 0" }}
                />
              </Link>

              <div className="hidden lg:flex">
                <ul
                  ref={navListRef}
                  className="relative flex gap-2"
                  onMouseLeave={handleNavItemsLeave}
                >
                  {navItems.map((item, index) => (
                    <li
                      key={index}
                      className="relative"
                      onMouseEnter={() => handleNavItemEnter(index)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <Link
                        ref={(node) => setNavLinkRef(index, node)}
                        to={item.disableNavigation ? "#" : item.path}
                        className="px-4 py-2 text-sm uppercase font-medium text-gray-800 hover:text-pink-600 transition-colors duration-300 ease-out relative rounded-lg inline-block"
                        onFocus={() => {
                          setHoveredNavIndex(index);
                          updateNavUnderline(index);
                        }}
                        onClick={(e) => {
                          if (item.disableNavigation) {
                            e.preventDefault();
                            return;
                          }
                          trackEvent("nav_click", {
                            label: item.name,
                            path: item.path,
                          });
                        }}
                      >
                        {item.name}
                      </Link>

                      {item.submenu &&
                        item.submenu.length > 0 &&
                        activeDropdown === index && (
                          <div
                            className="absolute top-full left-0 pt-1 z-50"
                            onMouseEnter={() => handleDropdownEnter(index)}
                            onMouseLeave={handleDropdownLeave}
                          >
                            <div className="w-64 bg-white border border-gray-100 rounded-2xl shadow-2xl py-1">
                              {item.submenu.map((subItem, subIndex) => (
                                <div
                                  key={subIndex}
                                  className="relative"
                                  onMouseEnter={() =>
                                    handleNestedEnter(subIndex)
                                  }
                                  onMouseLeave={handleNestedLeave}
                                >
                                  <Link
                                    to={subItem.path}
                                    className={`flex items-center justify-between px-5 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-pink-50 hover:to-transparent hover:text-pink-600 transition-all duration-300 ease-out rounded-md mx-1 uppercase font-medium ${
                                      submenuItemsVisible
                                        ? "opacity-100 translate-y-0"
                                        : "opacity-0 translate-y-2"
                                    }`}
                                    style={{
                                      transitionDelay: submenuItemsVisible
                                        ? `${subIndex * 120}ms`
                                        : "0ms",
                                    }}
                                    onClick={() => {
                                      trackEvent("nav_click", {
                                        label: subItem.name,
                                        path: subItem.path,
                                      });
                                    }}
                                  >
                                    <span>{subItem.name}</span>
                                    {subItem.submenu &&
                                      subItem.submenu.length > 0 && (
                                        <ChevronRight className="w-4 h-4 transition-transform" />
                                      )}
                                  </Link>

                                  {subItem.submenu &&
                                    subItem.submenu.length > 0 &&
                                    activeNestedDropdown === subIndex && (
                                      <div
                                        className="absolute top-0 left-full pl-1 z-50"
                                        onMouseEnter={() =>
                                          handleNestedEnter(subIndex)
                                        }
                                        onMouseLeave={handleNestedLeave}
                                      >
                                        <div className="w-56 bg-white border border-gray-100 rounded-xl shadow-2xl py-1">
                                          {subItem.submenu.map(
                                            (nestedItem, idx) => (
                                              <Link
                                                key={idx}
                                                to={nestedItem.path}
                                                className={`block px-5 py-2 text-sm text-gray-600 hover:bg-pink-500 hover:text-white transition-all duration-300 ease-out rounded-md mx-1 uppercase font-medium ${
                                                  nestedSubmenuItemsVisible
                                                    ? "opacity-100 translate-y-0"
                                                    : "opacity-0 translate-y-2"
                                                }`}
                                                style={{
                                                  transitionDelay:
                                                    nestedSubmenuItemsVisible
                                                      ? `${idx * 110}ms`
                                                      : "0ms",
                                                }}
                                                onClick={() => {
                                                  trackEvent("nav_click", {
                                                    label: nestedItem.name,
                                                    path: nestedItem.path,
                                                  });
                                                }}
                                              >
                                                {nestedItem.name}
                                              </Link>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </li>
                  ))}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-0.5 left-0 h-px bg-pink-500 transition-[transform,width,opacity] duration-300 will-change-transform"
                    style={{
                      transform: `translate3d(${navUnderline.x}px, 0, 0)`,
                      width: `${navUnderline.width}px`,
                      opacity: navUnderline.visible ? 1 : 0,
                      transitionTimingFunction:
                        "cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  />
                </ul>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-6 flex-1 justify-center px-6 max-w-md">
              {" "}
              <div className="relative w-full">
                <div className="relative flex items-center bg-gradient-to-r from-gray-50 to-gray-100 rounded-full px-4 py-2.5 border border-gray-200 hover:border-pink-300 transition-colors">
                  <Search className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                  <input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    type="text"
                    placeholder=""
                    className="bg-transparent outline-none text-gray-700 w-full placeholder-gray-400 text-sm font-medium"
                  />
                  {searchValue && (
                    <button
                      onClick={handleSearchClear}
                      className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {!searchValue && !showSearchResults && (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-11 text-sm text-gray-400 overflow-hidden whitespace-nowrap">
                      {(() => {
                        const { before, highlight, after } =
                          highlightPlaceholderPieces(placeholder);
                        return (
                          <>
                            <span>{before}</span>
                            {highlight && (
                              <span className="font-bold text-pink-600">
                                {highlight}
                              </span>
                            )}
                            <span>{after}</span>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {showSearchResults && (
                  <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-2xl shadow-2xl max-h-96 overflow-y-auto z-50">
                    {searchLoading ? (
                      <div className="px-4 py-8 text-center">
                        <div className="inline-block">
                          <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                          Searching...
                        </p>
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((product) => (
                        <button
                          key={product._id}
                          onClick={() => handleProductClick(product._id)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-pink-50 border-b border-gray-100 last:border-b-0 transition-colors text-left"
                        >
                          <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                                <Search className="w-6 h-6" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                                {product.name}
                              </p>
                              {product.category && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {product.category}
                                </p>
                              )}
                            </div>
                            {product.price && (
                              <span className="text-sm font-bold text-pink-600 flex-shrink-0">
                                ₹{product.price}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          No products found
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Try searching for something else
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {showRecommendedBuffer && !searchValue && (
                  <div className="absolute top-full left-0 mt-2 w-[800px] -translate-x-1/3 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
                    <div className="py-16 text-center">
                      <div className="inline-block">
                        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-4 font-medium">
                        Loading recommendations...
                      </p>
                    </div>
                  </div>
                )}

                {showRecommended && !searchValue && (
                  <div className="absolute top-full left-0 mt-2 w-[800px] -translate-x-1/3 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
                    {recommendedLoading ? (
                      <div className="py-12 text-center">
                        <div className="inline-block">
                          <div className="w-8 h-8 border-3 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-3 font-medium">
                          Loading recommendations...
                        </p>
                      </div>
                    ) : recommendedProducts.length > 0 ? (
                      <>
                        <div className="bg-gradient-to-r from-pink-50 to-purple-50 px-6 py-3 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-pink-600 rounded-full"></span>
                                RECOMMENDED FOR YOU
                              </h3>
                              <p className="text-xs text-gray-600 mt-0.5">
                                Handpicked products just for you
                              </p>
                            </div>
                            <Link
                              to="/recommended"
                              onClick={() => setShowRecommended(false)}
                              className="text-xs font-semibold text-pink-600 hover:text-pink-700 flex items-center gap-1 group"
                            >
                              Explore all picks
                              <ChevronRight
                                size={14}
                                className="group-hover:translate-x-0.5 transition-transform"
                              />
                            </Link>
                          </div>
                        </div>

                        <div className="relative px-6 py-5">
                          <div
                            ref={carouselRef}
                            className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory"
                          >
                            {recommendedProducts.map((product) => {
                              const displayPrice =
                                product.pricing?.discountedPrice ||
                                product.discountedPrice ||
                                product.pricing?.price ||
                                product.price;
                              const originalPrice =
                                product.pricing?.price || product.price;
                              const hasDiscount =
                                product.pricing?.discountedPrice ||
                                product.discountedPrice;
                              const discount = hasDiscount
                                ? Math.round(
                                    ((originalPrice - displayPrice) /
                                      originalPrice) *
                                      100,
                                  )
                                : 0;

                              return (
                                <button
                                  key={product._id}
                                  onClick={() =>
                                    handleProductClick(product._id)
                                  }
                                  className="w-44 flex-shrink-0 text-left group snap-start"
                                >
                                  <div className="relative w-full aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden mb-2 shadow-md group-hover:shadow-xl transition-all duration-300">
                                    <img
                                      src={
                                        product.media?.mainImage ||
                                        product.photo ||
                                        "/placeholder.jpg"
                                      }
                                      alt={
                                        product.basic?.productName ||
                                        product.name
                                      }
                                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    {discount > 0 && (
                                      <div className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-1.5 py-0.5 rounded text-[10px] font-bold shadow-lg">
                                        {discount}% OFF
                                      </div>
                                    )}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300"></div>
                                  </div>

                                  <div className="space-y-1">
                                    <p className="text-[11px] font-semibold text-gray-800 line-clamp-2 leading-tight group-hover:text-pink-600 transition-colors">
                                      {product.basic?.productName ||
                                        product.name}
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-xs font-bold text-pink-600">
                                        ₹{displayPrice?.toLocaleString("en-IN")}
                                      </p>
                                      {hasDiscount && (
                                        <p className="text-[10px] text-gray-500 line-through">
                                          ₹
                                          {originalPrice?.toLocaleString(
                                            "en-IN",
                                          )}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {canScrollLeft && (
                            <button
                              onClick={() => scrollCarousel(-1)}
                              className="absolute left-1 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm border-2 border-gray-300 rounded-full p-1.5 transition-all shadow-lg hover:bg-pink-50 hover:border-pink-500 hover:shadow-xl"
                            >
                              <ChevronLeft
                                size={16}
                                className="text-gray-700"
                              />
                            </button>
                          )}

                          {canScrollRight && (
                            <button
                              onClick={() => scrollCarousel(1)}
                              className="absolute right-10 top-1/3 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm border-2 border-gray-300 rounded-full p-1.5 transition-all shadow-lg hover:bg-pink-200 hover:border-pink-500 hover:shadow-xl"
                            >
                              <ChevronRight
                                size={16}
                                className="text-gray-700"
                              />
                            </button>
                          )}
                        </div>

                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-center">
                          <Link
                            to="/recommended"
                            onClick={() => setShowRecommended(false)}
                            className="px-8 py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full font-semibold text-sm hover:from-pink-600 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
                          >
                            Product Only for you
                          </Link>
                        </div>
                      </>
                    ) : (
                      <div className="py-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Search className="w-8 h-8 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">
                          No recommended products
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Check back soon for personalized recommendations
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center gap-6 min-w-max">
              <span
                className="w-5 h-5 text-gray-700 cursor-pointer hover:text-pink-600 hover:scale-110 transition-all"
                onClick={() => navigate("/profile")}
                title="Profile"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 512 512"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0 C0.56702637 0.34836914 1.13405273 0.69673828 1.71826172 1.05566406 C17.07452514 10.69868061 30.78530952 24.38773348 40 40 C40.37560059 40.63341309 40.75120117 41.26682617 41.13818359 41.91943359 C58.55070951 71.71929783 62.66942772 105.63446491 53.96875 139.02734375 C45.48050514 169.6093934 24.50103138 196.31540848 -2.875 212.1875 C-30.4303499 227.60416188 -65.0109565 233.53970253 -95.890625 225.2578125 C-97.26351403 224.84879788 -98.63341041 224.42959299 -100 224 C-100.68900391 223.78569336 -101.37800781 223.57138672 -102.08789062 223.35058594 C-121.26976811 217.17535171 -137.36067891 206.77967183 -152 193 C-152.59167969 192.47277344 -153.18335938 191.94554687 -153.79296875 191.40234375 C-174.02854263 172.60873476 -186.73013174 142.55441662 -188.14233398 115.24780273 C-188.617006 100.33971951 -187.88950111 86.42262045 -184 72 C-183.82871582 71.34032227 -183.65743164 70.68064453 -183.48095703 70.00097656 C-175.73828084 41.06820961 -155.247266 15.69063003 -130 0 C-129.42878418 -0.35642578 -128.85756836 -0.71285156 -128.26904297 -1.08007812 C-89.60800321 -24.62136726 -38.28206408 -23.81367978 0 0 Z M-129 42 C-129.76957031 42.76957031 -130.53914063 43.53914063 -131.33203125 44.33203125 C-147.97471456 61.88198179 -155.56034356 84.08743565 -155.421875 108.01220703 C-155.03583441 122.02903604 -151.63413643 134.63127106 -145 147 C-144.49855469 147.99257812 -143.99710938 148.98515625 -143.48046875 150.0078125 C-132.56562225 170.35032447 -112.83276125 185.05164443 -91.09570312 192.11572266 C-68.02717283 198.95964533 -43.56263526 196.45104335 -22.32348633 185.17797852 C-15.26242554 181.20643774 -9.02293764 176.3920571 -3 171 C-2.36707031 170.44957031 -1.73414063 169.89914063 -1.08203125 169.33203125 C14.50823751 154.883384 24.41916718 132.20807696 25.22265625 111.16796875 C25.88952287 82.94503609 16.74638546 60.48387113 -2.625 39.9375 C-19.16191096 23.75676866 -41.82697196 15.29004817 -64.75 14.6875 C-89.60290684 15.1559029 -111.41726579 24.41726579 -129 42 Z "
                    fill="#000"
                    transform="translate(321,44)"
                  />
                  <path
                    d="M0 0 C0.96594086 0.00054382 1.93188171 0.00108765 2.92709351 0.00164795 C18.82562753 0.04267558 34.10512663 0.97210178 49.6875 4.375 C51.17467529 4.69645996 51.17467529 4.69645996 52.69189453 5.02441406 C72.69584057 9.49868101 91.66620875 16.63246438 109.6875 26.375 C110.2861084 26.69436523 110.8847168 27.01373047 111.50146484 27.34277344 C150.16590155 48.1029098 181.74964144 82.67940879 194.9375 124.9375 C199.12046644 139.02024457 203.66329972 157.69713043 199.625 172.1875 C196.70193845 176.99640771 193.07686484 180.57854505 187.6875 182.375 C181.7047376 182.96154533 177.58508878 181.68619589 172.6875 178.375 C169.28841212 174.71444382 167.63981114 172.14928038 167.29296875 167.21875 C165.87152279 148.93780941 162.86876038 132.87111117 154.6875 116.375 C154.2028125 115.37855469 153.718125 114.38210938 153.21875 113.35546875 C134.88184782 77.75131701 98.65218487 54.80899529 61.625 42.68359375 C10.62280467 26.77501047 -45.00265831 31.65674959 -92.38671875 56.23828125 C-102.40601071 61.59295098 -111.68073711 67.98655395 -120.3125 75.375 C-121.13234375 76.07238281 -121.9521875 76.76976562 -122.796875 77.48828125 C-148.1678544 99.66819026 -162.8661229 128.47836559 -165.61328125 162.05078125 C-165.71189453 163.60345703 -165.71189453 163.60345703 -165.8125 165.1875 C-166.22852975 170.92391024 -167.4273187 174.58903877 -171.6875 178.625 C-176.62414931 181.91609954 -180.33587071 182.89926573 -186.3125 182.375 C-191.68374524 180.51150675 -195.21958126 177.05971405 -198.3125 172.375 C-205.54043675 149.47978631 -192.38035872 117.84690037 -182.23046875 97.796875 C-161.53282937 58.4761688 -123.40425299 29.70861149 -82.3125 14.375 C-81.49346191 14.06723633 -80.67442383 13.75947266 -79.83056641 13.44238281 C-53.9653945 3.88709638 -27.46783662 -0.05837258 0 0 Z "
                    fill="#000"
                    transform="translate(255.3125,303.625)"
                  />
                </svg>
              </span>
              <span
                className="w-5 h-5 text-gray-700 cursor-pointer hover:text-pink-600 hover:scale-110 transition-all"
                onClick={() => navigate("/wishlist")}
                title="Wishlist"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <WishlistIcon />
              </span>
              <div className="relative">
                <span
                  className="w-5 h-5 text-gray-700 cursor-pointer hover:text-pink-600 hover:scale-110 transition-all"
                  onClick={() => {
                    trackCartIconClick();
                    setCartOpen(true);
                  }}
                  title="Cart"
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                    />
                  </svg>
                </span>
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                  {token ? count : 0}
                </span>
                <CartDrawer
                  open={cartOpen}
                  onClose={() => setCartOpen(false)}
                />
              </div>
            </div>

            <div className="md:hidden flex items-center gap-4">
              <button
                onClick={() => setMobileSearchOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 text-gray-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </button>

              <span
                className="w-5 h-5 text-gray-700 cursor-pointer hover:text-pink-600 transition-all"
                onClick={() => navigate("/profile")}
                title="Profile"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 512 512"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0 C0.56702637 0.34836914 1.13405273 0.69673828 1.71826172 1.05566406 C17.07452514 10.69868061 30.78530952 24.38773348 40 40 C40.37560059 40.63341309 40.75120117 41.26682617 41.13818359 41.91943359 C58.55070951 71.71929783 62.66942772 105.63446491 53.96875 139.02734375 C45.48050514 169.6093934 24.50103138 196.31540848 -2.875 212.1875 C-30.4303499 227.60416188 -65.0109565 233.53970253 -95.890625 225.2578125 C-97.26351403 224.84879788 -98.63341041 224.42959299 -100 224 C-100.68900391 223.78569336 -101.37800781 223.57138672 -102.08789062 223.35058594 C-121.26976811 217.17535171 -137.36067891 206.77967183 -152 193 C-152.59167969 192.47277344 -153.18335938 191.94554687 -153.79296875 191.40234375 C-174.02854263 172.60873476 -186.73013174 142.55441662 -188.14233398 115.24780273 C-188.617006 100.33971951 -187.88950111 86.42262045 -184 72 C-183.82871582 71.34032227 -183.65743164 70.68064453 -183.48095703 70.00097656 C-175.73828084 41.06820961 -155.247266 15.69063003 -130 0 C-129.42878418 -0.35642578 -128.85756836 -0.71285156 -128.26904297 -1.08007812 C-89.60800321 -24.62136726 -38.28206408 -23.81367978 0 0 Z M-129 42 C-129.76957031 42.76957031 -130.53914063 43.53914063 -131.33203125 44.33203125 C-147.97471456 61.88198179 -155.56034356 84.08743565 -155.421875 108.01220703 C-155.03583441 122.02903604 -151.63413643 134.63127106 -145 147 C-144.49855469 147.99257812 -143.99710938 148.98515625 -143.48046875 150.0078125 C-132.56562225 170.35032447 -112.83276125 185.05164443 -91.09570312 192.11572266 C-68.02717283 198.95964533 -43.56263526 196.45104335 -22.32348633 185.17797852 C-15.26242554 181.20643774 -9.02293764 176.3920571 -3 171 C-2.36707031 170.44957031 -1.73414063 169.89914063 -1.08203125 169.33203125 C14.50823751 154.883384 24.41916718 132.20807696 25.22265625 111.16796875 C25.88952287 82.94503609 16.74638546 60.48387113 -2.625 39.9375 C-19.16191096 23.75676866 -41.82697196 15.29004817 -64.75 14.6875 C-89.60290684 15.1559029 -111.41726579 24.41726579 -129 42 Z "
                    fill="#000"
                    transform="translate(321,44)"
                  />
                  <path
                    d="M0 0 C0.96594086 0.00054382 1.93188171 0.00108765 2.92709351 0.00164795 C18.82562753 0.04267558 34.10512663 0.97210178 49.6875 4.375 C51.17467529 4.69645996 51.17467529 4.69645996 52.69189453 5.02441406 C72.69584057 9.49868101 91.66620875 16.63246438 109.6875 26.375 C110.2861084 26.69436523 110.8847168 27.01373047 111.50146484 27.34277344 C150.16590155 48.1029098 181.74964144 82.67940879 194.9375 124.9375 C199.12046644 139.02024457 203.66329972 157.69713043 199.625 172.1875 C196.70193845 176.99640771 193.07686484 180.57854505 187.6875 182.375 C181.7047376 182.96154533 177.58508878 181.68619589 172.6875 178.375 C169.28841212 174.71444382 167.63981114 172.14928038 167.29296875 167.21875 C165.87152279 148.93780941 162.86876038 132.87111117 154.6875 116.375 C154.2028125 115.37855469 153.718125 114.38210938 153.21875 113.35546875 C134.88184782 77.75131701 98.65218487 54.80899529 61.625 42.68359375 C10.62280467 26.77501047 -45.00265831 31.65674959 -92.38671875 56.23828125 C-102.40601071 61.59295098 -111.68073711 67.98655395 -120.3125 75.375 C-121.13234375 76.07238281 -121.9521875 76.76976562 -122.796875 77.48828125 C-148.1678544 99.66819026 -162.8661229 128.47836559 -165.61328125 162.05078125 C-165.71189453 163.60345703 -165.71189453 163.60345703 -165.8125 165.1875 C-166.22852975 170.92391024 -167.4273187 174.58903877 -171.6875 178.625 C-176.62414931 181.91609954 -180.33587071 182.89926573 -186.3125 182.375 C-191.68374524 180.51150675 -195.21958126 177.05971405 -198.3125 172.375 C-205.54043675 149.47978631 -192.38035872 117.84690037 -182.23046875 97.796875 C-161.53282937 58.4761688 -123.40425299 29.70861149 -82.3125 14.375 C-81.49346191 14.06723633 -80.67442383 13.75947266 -79.83056641 13.44238281 C-53.9653945 3.88709638 -27.46783662 -0.05837258 0 0 Z "
                    fill="#000"
                    transform="translate(255.3125,303.625)"
                  />
                </svg>
              </span>

              <span
                className="w-5 h-5 text-gray-700 cursor-pointer hover:text-pink-600 transition-all"
                onClick={() => navigate("/wishlist")}
                title="Wishlist"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <WishlistIcon />
              </span>

              <div className="relative">
                <span
                  className="w-5 h-5 text-gray-700 cursor-pointer hover:text-pink-600 transition-all"
                  onClick={() => {
                    trackCartIconClick();
                    setCartOpen(true);
                  }}
                  title="Cart"
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                    />
                  </svg>
                </span>
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                  {token ? count : 0}
                </span>
                <CartDrawer
                  open={cartOpen}
                  onClose={() => setCartOpen(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navItems={navItems}
      />

      {mobileSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
          <div className="bg-white w-full pt-4 px-4 pb-6 rounded-b-2xl min-h-[70vh]">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setMobileSearchOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
              <div className="relative flex-1">
                <div className="relative flex items-center bg-gray-100 rounded-full px-3 py-2.5">
                  <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <input
                    autoFocus
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onFocus={handleSearchFocus}
                    type="text"
                    placeholder="Search products..."
                    className="bg-transparent outline-none text-gray-700 w-full text-sm"
                  />
                </div>
              </div>
            </div>

            {!searchValue && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                    Recommended for you
                  </h3>
                  <Link
                    to="/recommended"
                    onClick={() => setMobileSearchOpen(false)}
                    className="text-xs font-semibold text-pink-600"
                  >
                    View all
                  </Link>
                </div>

                {recommendedLoading ? (
                  <div className="py-6 text-center">
                    <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : recommendedProducts.length > 0 ? (
                  <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                    {recommendedProducts.map((product) => (
                      <button
                        key={product._id}
                        onClick={() => handleProductClick(product._id)}
                        className="text-left flex-shrink-0 w-[45vw] snap-start"
                      >
                        <div className="w-full aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-2">
                          <img
                            src={
                              product.media?.mainImage ||
                              product.photo ||
                              "/placeholder.jpg"
                            }
                            alt={product.basic?.productName || product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-xs font-semibold text-gray-800 line-clamp-2">
                          {product.basic?.productName || product.name}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    No recommendations yet.
                  </p>
                )}
              </div>
            )}

            {showSearchResults && (
              <div className="max-h-96 overflow-y-auto mb-4 rounded-lg border border-gray-100 mt-4">
                {searchLoading ? (
                  <div className="px-4 py-6 text-center">
                    <div className="inline-block">
                      <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <button
                      key={product._id}
                      onClick={() => handleProductClick(product._id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-pink-50 border-b border-gray-100 last:border-b-0 transition-colors text-left"
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-gray-200 rounded overflow-hidden">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 line-clamp-1">
                          {product.name}
                        </p>
                        {product.price && (
                          <p className="text-sm font-bold text-pink-600">
                            ₹{product.price}
                          </p>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-gray-500">
                    No products found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NewNavbar;
